/** Script → Storyboard entity + reference inheritance.
 * Characters/products are first-class entities; image assets bind as
 * references — never peer "关联资产" chips of equal rank.
 *
 * Character card (正面/侧面/全身) remains optional; primary reference
 * image alone is enough for downstream keyframe/video.
 */

import type { ScriptArtifact, ShotArtifact } from '../../../api'
import { partitionScriptEntities } from './scriptGenerationCanvas'

export type StoryboardEntityKind = 'character' | 'product' | 'scene'

export interface StoryboardSupportingAssetLike {
  asset_id?: string | null
  picker_item_id?: string | null
  asset_type?: string
  display_name?: string
  inherited?: boolean
  derivedFrom?: string | null
}

export interface StoryboardEntityReference {
  asset_id?: string | null
  picker_item_id?: string | null
  display_name: string
  asset_type: string
}

export interface StoryboardEntityBinding {
  entity_id: string
  kind: StoryboardEntityKind
  name: string
  /** Primary visual reference (e.g. 模特图片). */
  primary_reference: StoryboardEntityReference | null
  /** Optional generated character card — never required for generate. */
  character_card_asset: StoryboardEntityReference | null
  /** Product: which character's outfit this product visually comes from. */
  visual_source_character_id: string | null
  visual_source_label: string | null
}

const CHARACTER_TYPES = new Set(['character', 'virtual_ip', 'vip'])
const PRODUCT_TYPES = new Set(['product', 'prop', 'object', 'key_object'])
const IMAGE_TYPES = new Set(['image_asset', 'image', 'photo', 'reference_image', 'unknown'])

function slugEntityId(kind: StoryboardEntityKind, name: string): string {
  const safe = name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^\w\u4e00-\u9fff\-]/g, '')
    .slice(0, 48)
  return `${kind}_${safe || 'unnamed'}`
}

function isImageLike(a: StoryboardSupportingAssetLike): boolean {
  const t = String(a.asset_type || '').toLowerCase()
  if (CHARACTER_TYPES.has(t) || PRODUCT_TYPES.has(t)) return false
  if (IMAGE_TYPES.has(t) || t === '' || t === 'media') return true
  // Named image uploads often land as image_asset; anything with a real
  // asset_id that isn't typed as character/product is a visual reference.
  return Boolean(a.asset_id || a.picker_item_id)
}

function toReference(a: StoryboardSupportingAssetLike): StoryboardEntityReference {
  return {
    asset_id: a.asset_id ?? null,
    picker_item_id: a.picker_item_id ?? null,
    display_name: String(a.display_name || a.asset_id || '参考图'),
    asset_type: String(a.asset_type || 'image_asset'),
  }
}

/**
 * Derive entity bindings from Script character/product names + supporting
 * assets. Image assets become references under characters/products — they
 * are not returned as top-level peer entities.
 */
export function resolveStoryboardEntityBindings(args: {
  script?: Pick<ScriptArtifact, 'characters' | 'key_objects'> | null
  supportingAssets?: StoryboardSupportingAssetLike[] | null
}): StoryboardEntityBinding[] {
  const assets = Array.isArray(args.supportingAssets) ? args.supportingAssets : []
  const { characters, keyObjects } = partitionScriptEntities(args.script, assets)

  const imageRefs = assets.filter(isImageLike).map(toReference)
  const typedCharacterAssets = assets.filter((a) => CHARACTER_TYPES.has(String(a.asset_type || '').toLowerCase()))
  const typedProductAssets = assets.filter((a) => PRODUCT_TYPES.has(String(a.asset_type || '').toLowerCase()))

  const characterNames: string[] = []
  const addChar = (n: string) => {
    const name = n.trim()
    if (!name || characterNames.includes(name)) return
    characterNames.push(name)
  }
  for (const n of characters) addChar(n)
  for (const a of typedCharacterAssets) addChar(String(a.display_name || '').trim())

  const productNames: string[] = []
  const addProd = (n: string) => {
    const name = n.trim()
    if (!name || productNames.includes(name) || characterNames.includes(name)) return
    productNames.push(name)
  }
  for (const n of keyObjects) addProd(n)
  for (const a of typedProductAssets) addProd(String(a.display_name || '').trim())

  // Prefer first concrete image as shared wardrobe/model reference.
  const sharedPrimary = imageRefs[0] ?? null

  const characterBindings: StoryboardEntityBinding[] = characterNames.map((name) => {
    const typed = typedCharacterAssets.find((a) => String(a.display_name || '').trim() === name)
    // Character asset row with its own asset_id (VIP photo) wins; else shared model photo.
    const primary =
      typed && (typed.asset_id || typed.picker_item_id) ? toReference(typed) : sharedPrimary
    return {
      entity_id: slugEntityId('character', name),
      kind: 'character',
      name,
      primary_reference: primary,
      character_card_asset: null,
      visual_source_character_id: null,
      visual_source_label: null,
    }
  })

  const leadCharacter = characterBindings[0] ?? null
  const productBindings: StoryboardEntityBinding[] = productNames.map((name) => {
    const typed = typedProductAssets.find((a) => String(a.display_name || '').trim() === name)
    const ownRef = typed && (typed.asset_id || typed.picker_item_id) ? toReference(typed) : null
    return {
      entity_id: slugEntityId('product', name),
      kind: 'product',
      name,
      primary_reference: ownRef || leadCharacter?.primary_reference || sharedPrimary,
      character_card_asset: null,
      visual_source_character_id: leadCharacter?.entity_id ?? null,
      visual_source_label: leadCharacter
        ? `${leadCharacter.name} 穿着服装`
        : ownRef
          ? null
          : '模特穿着服装',
    }
  })

  return [...characterBindings, ...productBindings]
}

/** Asset ids that are only references under entities — hide from flat peer list. */
export function referencedImageAssetKeys(bindings: StoryboardEntityBinding[]): Set<string> {
  const keys = new Set<string>()
  for (const b of bindings) {
    const ref = b.primary_reference
    if (!ref) continue
    if (ref.asset_id) keys.add(ref.asset_id)
    if (ref.picker_item_id) keys.add(ref.picker_item_id)
    keys.add(`name:${ref.display_name}`)
  }
  return keys
}

/**
 * Flat supportingAssets that should still appear as loose chips —
 * only assets NOT consumed as an entity reference and NOT already an
 * entity name row (character/product typed without image).
 */
export function orphanSupportingAssets(
  assets: StoryboardSupportingAssetLike[],
  bindings: StoryboardEntityBinding[],
): StoryboardSupportingAssetLike[] {
  const refKeys = referencedImageAssetKeys(bindings)
  const entityNames = new Set(bindings.map((b) => b.name))
  return assets.filter((a) => {
    const name = String(a.display_name || '').trim()
    const t = String(a.asset_type || '').toLowerCase()
    if (CHARACTER_TYPES.has(t) || PRODUCT_TYPES.has(t)) return false
    if (entityNames.has(name)) return false
    if (a.asset_id && refKeys.has(a.asset_id)) return false
    if (a.picker_item_id && refKeys.has(a.picker_item_id)) return false
    if (name && refKeys.has(`name:${name}`)) return false
    return true
  })
}

/** Resolve character/product name → primary reference asset_id. */
export function resolveEntityReferenceAssetId(
  bindings: StoryboardEntityBinding[],
  nameOrId: string | null | undefined,
): string | null {
  const key = String(nameOrId || '').trim()
  if (!key) return null
  const hit =
    bindings.find((b) => b.entity_id === key || b.name === key) ||
    bindings.find((b) => b.name.toLowerCase() === key.toLowerCase())
  return hit?.primary_reference?.asset_id ?? null
}

/**
 * Enrich shot character/product refs with inherited asset_ids from the
 * entity registry — does not invent new entities.
 */
export function enrichShotWithEntityBindings(
  shot: ShotArtifact,
  bindings: StoryboardEntityBinding[],
): ShotArtifact {
  if (!bindings.length) return shot
  const characters = (shot.characters || []).map((c) => {
    if (c.asset_id) return c
    const asset_id = resolveEntityReferenceAssetId(bindings, c.name || c.character_id)
    return asset_id ? { ...c, asset_id } : c
  })
  const products = (shot.products || []).map((p) => {
    if (p.asset_id) return p
    const asset_id = resolveEntityReferenceAssetId(bindings, p.name || p.product_id)
    return asset_id ? { ...p, asset_id } : p
  })
  return { ...shot, characters, products }
}

export function enrichStoryboardShotsWithEntityBindings(
  shots: ShotArtifact[],
  bindings: StoryboardEntityBinding[],
): ShotArtifact[] {
  return shots.map((s) => enrichShotWithEntityBindings(s, bindings))
}
